import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Check, ChevronDown, ChevronUp } from 'lucide-react';
import '../styles/NavChangeNotification.css';
import { formatLastModified } from './NestedFeed';

const NavChangeNotification = () => {
    const [updates, setUpdates] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [loading, setLoading] = useState(true);

    const formatUpdateDate = (value) => {
        if (!value) return '';
        const [longDate, , timeFormatted] = formatLastModified(String(value));
        if (!longDate || !timeFormatted) return String(value);
        return `${longDate} ${timeFormatted}`;
    };

    const formatUpdateBody = (value) => {
        if (!value) return '';

        return String(value)
            .replace(/\r\n/g, '\n')
            .split(/(\n+)/)
            .map((part) => (part === '\n' ? '\n\n' : part))
            .join('');
    };

    const fetchUpdates = useCallback(async () => {
        try {
            const response = await fetch('https://api.casedb.co/system_updates.php', { credentials: 'include' });
            const data = await response.json();
            if (data?.success && Array.isArray(data.updates)) {
                setUpdates(data.updates);
            }
        } catch (error) {
            console.error('Error fetching system updates:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUpdates();
    }, [fetchUpdates]);

    const markAllRead = async () => {
        const updateIds = updates.map(update => update.id).filter(Boolean);
        if (updateIds.length === 0) {
            setHidden(true);
            return;
        }

        try {
            const response = await fetch('https://api.casedb.co/system_updates.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ update_ids: updateIds })
            });
            const data = await response.json();
            if (data?.success) {
                setUpdates([]);
                setHidden(true);
            }
        } catch (error) {
            console.error('Error marking updates as read:', error);
        }
    };

    const markUpdateRead = async (updateId) => {
        if (!updateId) return;

        const removedUpdate = updates.find(update => update.id === updateId);
        if (!removedUpdate) return;

        setUpdates(prev => prev.filter(update => update.id !== updateId));

        try {
            const response = await fetch('https://api.casedb.co/system_updates.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ update_ids: [updateId] })
            });
            const data = await response.json();
            if (!data?.success) {
                setUpdates(prev => {
                    if (prev.some(update => update.id === updateId)) return prev;
                    return [...prev, removedUpdate].sort((left, right) => {
                        const leftDate = left.created_at_local || left.created_at || '';
                        const rightDate = right.created_at_local || right.created_at || '';
                        return String(rightDate).localeCompare(String(leftDate));
                    });
                });
            }
        } catch (error) {
            setUpdates(prev => {
                if (prev.some(update => update.id === updateId)) return prev;
                return [...prev, removedUpdate].sort((left, right) => {
                    const leftDate = left.created_at_local || left.created_at || '';
                    const rightDate = right.created_at_local || right.created_at || '';
                    return String(rightDate).localeCompare(String(leftDate));
                });
            });
            console.error('Error marking update as read:', error);
        }
    };

    if (loading || hidden || updates.length === 0) return null;

    const countLabel = updates.length === 1 ? '1 New Update' : `${updates.length} New Updates`;

    return (
        <div className="nav-change-notification system-updates">
            <div className="nav-change-content system-updates-content">
                <Bell size={20} className="nav-change-icon" />
                <div className="nav-change-text">
                    <div className="system-updates-header">
                        <div className="system-updates-header-text">
                            <strong>{countLabel}</strong>
                            <span>See what’s new in CaseDB.</span>
                        </div>
                        <div className="system-updates-actions">
                            <button
                                onClick={() => setExpanded(prev => !prev)}
                                className="nav-change-dismiss icon-only"
                                title={expanded ? 'Collapse' : 'Expand'}
                            >
                                {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                            <button
                                onClick={markAllRead}
                                className="nav-change-dismiss icon-only"
                                title="Mark all read"
                            >
                                <Check size={18} />
                            </button>
                        </div>
                    </div>
                    {expanded && (
                        <div className="system-updates-list">
                            {updates.map((update) => (
                                <div key={update.id} className="system-update-item">
                                    <div className="system-update-title-row">
                                        <div className="system-update-title">{update.title}</div>
                                        <button
                                            className="system-update-mark"
                                            type="button"
                                            onClick={() => markUpdateRead(update.id)}
                                            title="Mark seen"
                                        >
                                            Mark seen
                                        </button>
                                    </div>
                                    {update.body && <div className="system-update-body">{formatUpdateBody(update.body)}</div>}
                                    {update.link_url && (
                                        <a
                                            className="system-update-link"
                                            href={update.link_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={() => markUpdateRead(update.id)}
                                        >
                                            View details
                                        </a>
                                    )}
                                    {(update.created_at || update.created_at_local) && (
                                        <div className="system-update-date">
                                            {formatUpdateDate(update.created_at || update.created_at_local)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NavChangeNotification;
